import React, { useState, useRef, useEffect } from 'react';
import { FaChevronDown, FaTimes } from 'react-icons/fa';

const MultiSelectDropdown = ({
  options,
  selectedValues = [],
  onSelectionChange,
  placeholder = "Select options...",
  label,
  hasError = false,
  errorMessage = "",
  disabled = false,
  dropdownClassName = "",
  isDarkMode = false, // Critical for dark mode support
  isLoading = false,
  loadingText = "Loading..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (!disabled && !isLoading) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (value) => {
    let newSelection;
    const stringValue = String(value);
    if (selectedValues.includes(stringValue)) {
      newSelection = selectedValues.filter((v) => v !== stringValue);
    } else {
      newSelection = [...selectedValues, stringValue];
    }
    onSelectionChange(newSelection);
  };

  const removeChip = (valueToRemove) => {
    if (!disabled) {
      const newSelection = selectedValues.filter((v) => v !== valueToRemove);
      onSelectionChange(newSelection);
    }
  };

  const getOptionLabel = (value) => {
    const option = options.find(o => String(o.value) === String(value));
    return option ? option.label : value;
  };

  const hasSelectedOptions = selectedValues.length > 0;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Label */}
      {label && (
        <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
          isDarkMode ? 'text-gray-200' : 'text-gray-700'
        }`}>
          {label}
          {hasError && errorMessage && (
            <span className="text-red-500 text-xs ml-1"> {errorMessage}</span>
          )}
        </label>
      )}

      {/* Main Dropdown Trigger */}
      <div
        className={`relative w-full border rounded-md p-1.5 min-h-[40px] flex flex-wrap items-center gap-2 cursor-pointer transition-all duration-300
          ${hasError ? 'border-red-500 ring-2 ring-red-500 ring-opacity-30' : ''}
          ${disabled || isLoading 
            ? (isDarkMode ? 'bg-gray-800 border-gray-700 cursor-not-allowed opacity-70' : 'bg-gray-50 border-gray-300 cursor-not-allowed') 
            : (isDarkMode 
                ? 'bg-gray-900 border-gray-600 hover:border-blue-500 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/30' 
                : 'bg-white border-gray-300 hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/30')
          }
          ${dropdownClassName}`}
        onClick={toggleDropdown}
      >
        {/* Placeholder or Loading */}
        {!hasSelectedOptions && !isLoading && (
          <span className={`text-sm ml-1 transition-colors duration-300 ${
            isDarkMode ? 'text-gray-500' : 'text-gray-500'
          }`}>
            {placeholder}
          </span>
        )}

        {isLoading && (
          <span className={`text-sm ml-1 animate-pulse transition-colors duration-300 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {loadingText}
          </span>
        )}

        {/* Selected Chips */}
        <div className="flex flex-wrap gap-2 pr-8">
          {selectedValues.map((value) => (
            <span
              key={value}
              className={`flex items-center text-sm font-medium px-2.5 py-1 rounded-full transition-all duration-200 ${
                isDarkMode
                  ? 'bg-blue-900/70 text-blue-200 border border-blue-800'
                  : 'bg-blue-100 text-blue-800 border border-blue-200'
              }`}
            >
              {getOptionLabel(value)}
              {!disabled && !isLoading && (
                <FaTimes
                  className={`ml-1.5 cursor-pointer text-xs transition-colors duration-200 ${
                    isDarkMode ? 'hover:text-white' : 'hover:text-blue-900'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeChip(value);
                  }}
                />
              )}
            </span>
          ))}
        </div>

        {/* Chevron Icon */}
        <FaChevronDown
          className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          } ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
        />
      </div>

      {/* Dropdown Menu */}
      {isOpen && !disabled && !isLoading && (
        <div
          className={`absolute z-50 w-full mt-1 border rounded-md shadow-2xl max-h-60 overflow-y-auto transition-all duration-200 ${
            isDarkMode
              ? 'bg-gray-800 border-gray-700 shadow-black/50'
              : 'bg-white border-gray-300 shadow-lg'
          }`}
        >
          {options.length > 0 ? (
            options.map((option) => {
              const valueStr = String(option.value);
              const isSelected = selectedValues.includes(valueStr);

              return (
                <div
                  key={valueStr}
                  className={`px-3 py-2.5 cursor-pointer text-sm flex items-center transition-all duration-150 ${
                    isSelected
                      ? isDarkMode
                        ? 'bg-blue-900 text-blue-100'
                        : 'bg-blue-50 text-blue-700 font-medium'
                      : isDarkMode
                      ? 'hover:bg-gray-700 text-gray-300'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                  onClick={() => handleOptionClick(valueStr)}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    className={`mr-3 h-4 w-4 rounded border transition-colors duration-200 ${
                      isDarkMode
                        ? 'accent-blue-500 bg-gray-700 border-gray-600'
                        : 'accent-blue-600 bg-white border-gray-300'
                    }`}
                  />
                  <span className="select-none">{option.label}</span>
                </div>
              );
            })
          ) : (
            <div
              className={`px-3 py-2.5 text-sm text-center transition-colors duration-300 ${
                isDarkMode ? 'text-gray-500' : 'text-gray-500'
              }`}
            >
              No options available.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;